import { useCallback, useMemo, useRef, useState } from "react";
import type { AppEntry } from "../registry/apps";
import {
  appsForWall,
  nextShuffleSeed,
  orderApps,
  readShuffleSeed,
  readWallOrder,
  WALL_ORDER_KEY,
  writeShuffleSeed,
  writeWallOrder,
  type KeyValueStore,
  type WallOrder,
} from "./wall-order";

function browserStore(): KeyValueStore | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    // Older builds persisted RANDOM forever in localStorage; drop it so first visits are Featured.
    localStorage.removeItem(WALL_ORDER_KEY);
    return sessionStorage;
  } catch {
    return null;
  }
}

export function useWallOrder(apps: readonly AppEntry[]) {
  const [order, setOrder] = useState<WallOrder>(() => readWallOrder(browserStore()));
  const [seed, setSeed] = useState(() => readShuffleSeed(browserStore()));
  const orderRef = useRef(order);
  orderRef.current = order;

  const selectOrder = useCallback((next: WallOrder) => {
    if (next === "random" && orderRef.current === "random") {
      setSeed((prev) => {
        const n = nextShuffleSeed(prev);
        writeShuffleSeed(browserStore(), n);
        return n;
      });
      return;
    }
    writeWallOrder(browserStore(), next);
    setOrder(next);
  }, []);

  const reshuffle = useCallback(() => {
    setSeed((prev) => {
      const n = nextShuffleSeed(prev);
      writeShuffleSeed(browserStore(), n);
      return n;
    });
    writeWallOrder(browserStore(), "random");
    setOrder("random");
  }, []);

  const ordered = useMemo(
    () => orderApps(appsForWall(apps), order, seed),
    [apps, order, seed],
  );

  return { order, seed, ordered, selectOrder, reshuffle };
}
